import React from 'react';
import {useFocusHeadingAndAnnounce} from 'hooks/useFocusHeadingAndAnnounce';
import {ErrorArt, type ErrorSceneVariant} from './ErrorArt';
import {SceneHeader} from './SceneHeader';

type ErrorSceneProps = {
  variant: ErrorSceneVariant;
  title: string;
  subtitle: string;
  announce: string;
  artClassName?: string;
  children?: React.ReactNode;
};

function ErrorScene({
  variant,
  title,
  subtitle,
  announce,
  artClassName,
  children,
}: Readonly<ErrorSceneProps>) {
  const {headingRef, announcement} = useFocusHeadingAndAnnounce(announce);

  return (
    <div className="relative flex min-h-[var(--app-dvh,100dvh)] w-full flex-col bg-[var(--background-main)] lg:bg-[var(--chekin-neutral-50)]">
      <SceneHeader />

      <main className="flex flex-1 flex-col items-center justify-center gap-1.5 px-[26px] py-8 text-center">
        <ErrorArt variant={variant} className={artClassName} />

        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-[22px] text-[19px] font-bold leading-[1.28] tracking-[-0.01em] text-[color:var(--chekin-text-primary)] outline-none [text-wrap:balance] lg:text-[21px] lg:font-semibold lg:tracking-[-0.02em]"
        >
          {title}
        </h1>
        <p className="max-w-[300px] text-[13px] font-medium leading-[19px] text-[color:var(--chekin-text-secondary)] lg:max-w-[380px] lg:text-[14px] lg:leading-5">
          {subtitle}
        </p>

        {children}

        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>
      </main>
    </div>
  );
}

export {ErrorScene};
